import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/config/firebase";
import type { TeamCalculatorCardSnapshot } from "../components/TeamCalculatorCard/TeamCalculatorCard";

export type TeamCalculationSummary = {
  resourceCount: number;
  totalSaleValue: number;
  totalSpreadValue: number;
  spreadPercentage: number;
  averageSaleValue: number;
  averageSaleHourlyRate: number;
  totalMonthlyCost: number;
  averageHourlyCost: number;
  totalTaxes: number;
  isHoursModuleEnabled?: boolean;
};

export type TeamCalculationPayload = {
  title: string;
  cards: TeamCalculatorCardSnapshot[];
  summary: TeamCalculationSummary;
};

export type TeamCalculationDocument = TeamCalculationPayload & {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  createdByName?: string;
  createdByAvatarUrl?: string;
};

const getTeamCalculationsCollection = (userEmail: string) =>
  collection(db, "calculator_saves", userEmail, "teamCalculations");

const publicTeamCalculationsCollection = collection(db, "calculator_team_calculations");

export type SaveTeamCalculationInput = TeamCalculationPayload & {
  createdByName?: string;
  createdByAvatarUrl?: string;
};

export const saveTeamCalculation = async (
  userEmail: string,
  payload: SaveTeamCalculationInput,
  existingId?: string,
) => {
  const userCollection = getTeamCalculationsCollection(userEmail);
  const userDocRef = existingId ? doc(userCollection, existingId) : doc(userCollection);
  const id = userDocRef.id;

  const timestamp = serverTimestamp();
  const commonData: any = {
    ...payload,
    createdBy: userEmail,
    createdByName: payload.createdByName ?? null,
    createdByAvatarUrl: payload.createdByAvatarUrl ?? null,
    updatedAt: timestamp,
  };

  if (!existingId) {
    commonData.createdAt = timestamp;
  }

  // If we use setDoc with merge: true, fields not in data remain.
  // But we want to replace 'cards' fully.
  // So setDoc is correct. But we need to handle createdAt.

  if (existingId) {
    // We can rely on the fact that we don't want to change createdAt.
    // But if we overwrite, we lose it.
    // Let's use getDoc to fetch creation time? Or just ignore it?
    // It's displayed in lists.
    // Better approach: use updateDoc for existingId if meaningful.
    // However, setDoc with merge=true is safer for deep updates but might not delete removed cards.
    // Let's stick to setDoc but we must include createdAt if we want to keep it.
    // Actually, simplifying: just use setDoc with merge: true for now? No, cards array needs full replacement.
    // Best: If existingId check if we can just set it.
    // Effectively, for this task, standard practice if we don't have createdAt in payload is to keep it.
    // Let's try to get it first if it's an update.
    const snap = await getDoc(userDocRef);
    if (snap.exists()) {
      commonData.createdAt = snap.data().createdAt;
    } else {
      // specific case where valid ID passed but doc gone? fallback
      commonData.createdAt = timestamp;
    }
  }

  await Promise.all([
    setDoc(userDocRef, commonData),
    setDoc(doc(publicTeamCalculationsCollection, id), {
      ...commonData,
      ownerEmail: userEmail,
    }),
  ]);

  return id;
};

export const listTeamCalculations = async (userEmail: string): Promise<TeamCalculationDocument[]> => {
  const calculationsQuery = query(getTeamCalculationsCollection(userEmail), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(calculationsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data() as DocumentData;
    return {
      id: doc.id,
      title: data.title as string,
      cards: (data.cards ?? []) as TeamCalculatorCardSnapshot[],
      summary: (data.summary ?? {}) as TeamCalculationSummary,
      createdAt: data.createdAt as Timestamp,
      updatedAt: data.updatedAt as Timestamp,
      createdBy: data.createdBy as string,
      createdByName: data.createdByName as string | undefined,
      createdByAvatarUrl: data.createdByAvatarUrl as string | undefined,
    };
  });
};

export const getTeamCalculation = async (
  userEmail: string,
  calculationId: string,
): Promise<TeamCalculationDocument | null> => {
  const docRef = doc(db, "calculator_saves", userEmail, "teamCalculations", calculationId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as DocumentData;
  return {
    id: snapshot.id,
    title: data.title as string,
    cards: (data.cards ?? []) as TeamCalculatorCardSnapshot[],
    summary: (data.summary ?? {}) as TeamCalculationSummary,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    createdBy: data.createdBy as string,
    createdByName: data.createdByName as string | undefined,
    createdByAvatarUrl: data.createdByAvatarUrl as string | undefined,
  };
};

export const deleteTeamCalculation = async (userEmail: string, calculationId: string) => {
  const docRef = doc(db, "calculator_saves", userEmail, "teamCalculations", calculationId);
  await Promise.all([
    deleteDoc(docRef),
    deleteDoc(doc(publicTeamCalculationsCollection, calculationId)),
  ]);
};

export const listAllTeamCalculations = async (): Promise<TeamCalculationDocument[]> => {
  const calculationsQuery = query(publicTeamCalculationsCollection, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(calculationsQuery);

  return snapshot.docs.map((docSnapshot) => {
    const data = docSnapshot.data() as DocumentData;
    return {
      id: docSnapshot.id,
      title: data.title as string,
      cards: (data.cards ?? []) as TeamCalculatorCardSnapshot[],
      summary: (data.summary ?? {}) as TeamCalculationSummary,
      createdAt: data.createdAt as Timestamp,
      updatedAt: data.updatedAt as Timestamp,
      createdBy: data.createdBy as string,
      createdByName: data.createdByName as string | undefined,
      createdByAvatarUrl: data.createdByAvatarUrl as string | undefined,
    };
  });
};
